"use client";
import { useRef, useState } from "react";
import { validateEmail, validatePhone } from "~/app/utils/helper";
import LoadingWidget from "../../common/loadingWidget";
import CustomButton from "../../common/button";
import ErrorMessageStatus from "../../common/system-messages/errorMessage";
import SuccessMessageStatus from "../../common/system-messages/successMessage";
import ReCAPTCHA from "react-google-recaptcha";
import { useReCaptcha } from "next-recaptcha-v3";

type FormData = {
  firstname: string;
  lastname: string;
  phone: string;
  email: string;
  contactMethod: string;
  message: string;
};

enum FormStatus {
  IDLE,
  PENDING,
  SUCCESS,
  ERROR,
}

export default function ContactForm() {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [message, setMessage] = useState("");
  const [generalError, setGeneralError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<FormData>>({});
  const [formStatus, setFormStatus] = useState<FormStatus>(FormStatus.IDLE);
  // v2 recaptcha key vode
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isVerified, setIsverified] = useState<boolean>(false);
  async function handleCaptchaSubmission(token: string | null) {
    // Server function to verify captcha
    await fetch("/recaptcha-verify", {
      method: "POST",
      body: token,
    })
      .then(() => {
        setIsverified(true);
      })
      .catch(() => {
        setIsverified(false);
      });
  }

  // field validation
  const validateFormData = (data: FormData): Partial<FormData> | null => {
    const errors: Partial<FormData> = {};

    const emailError: string | undefined = validateEmail(data.email);
    const phoneError: string | undefined = validatePhone(data.phone);

    if (!data.firstname) errors.firstname = "First name is required.";
    if (!data.lastname) errors.lastname = "Last name is required.";
    if (emailError) errors.email = emailError;
    if (phoneError) errors.phone = phoneError;

    if (!data.message) {
      errors.message = "Message is required.";
    } else if (data.message.length > 500) {
      errors.message = "Message must be less than 500 characters.";
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  // v3 RECAPTCHA useReCaptcha hook - import 'executeRecaptcha' using 'useReCaptcha' hook
  const { executeRecaptcha } = useReCaptcha();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // if (!isVerified) {
    //   setGeneralError("Please complete the captcha.");
    //   return;
    // }

    const formData: FormData = {
      firstname,
      lastname,
      email,
      phone,
      contactMethod,
      message,
    };

    // Perform validation
    const validationErrors = validateFormData(formData);
    if (validationErrors) {
      setGeneralError("Please fill out all required fields correctly.");
      setFieldErrors(validationErrors);
      setFormStatus(FormStatus.IDLE);
      return;
    }

    // v3 RECAPTCHA - check the availability of execution before retrieving token
    if (!executeRecaptcha) {
      setGeneralError("Not available to execute recaptcha");
      setFormStatus(FormStatus.IDLE);
      return;
    }
    const token = await executeRecaptcha("contact_form_submit");
    // verify recaptcha before sending it to the server
    if (!token) {
      setGeneralError("reCAPTCHA verification failed, token is not available!");
      setFormStatus(FormStatus.IDLE);
      return;
    }

    // If validation passes, clear any previous errors and proceed with form submission
    setGeneralError("");
    setFieldErrors({});
    setFormStatus(FormStatus.PENDING);

    // check if form data is passing
    // console.log("form data: ", buildSendGridMessage(formData));

    // verify the reCAPTCHA token on the server
    try {
      const verifyRepsonse = await fetch("/api/verify-recaptcha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const verifyResult = (await verifyRepsonse.json()) as {
        success?: boolean;
        score?: number;
      };
      console.log("Verify Result: ", verifyResult); // testing

      if (!verifyResult.success) {
        setGeneralError(`reCAPTCHA verification failed, please try again.`);
        console.log("Failure with score: ", verifyResult.score);
        setIsverified(false);
        setFormStatus(FormStatus.IDLE);
        return;
      } else {
        setIsverified(true);
        console.log("Success with score: ", verifyResult.score);
      }
    } catch (error) {
      console.error("Error during reCAPTCHA verfication");
      setIsverified(false);
      setFormStatus(FormStatus.ERROR);
    }

    // SendGrid API call
    try {
      const resp = await fetch("/api/sendgrid", {
        method: "POST",
        body: JSON.stringify({
          msg: buildSendGridMessage(formData),
        }),
      });

      if (resp.ok) setFormStatus(FormStatus.SUCCESS);
      else setFormStatus(FormStatus.ERROR);
    } catch (e) {
      setFormStatus(FormStatus.ERROR);
    }
  };

  // form status
  if (formStatus === FormStatus.PENDING)
    return <LoadingWidget className="mt-2" />;
  if (formStatus === FormStatus.ERROR)
    return (
      <ErrorMessageStatus
        errorMessage="Unfortunately we are experiencing system errors right now and unable to process your request."
        className="mt-6"
      />
    );
  if (formStatus === FormStatus.SUCCESS)
    return (
      <SuccessMessageStatus
        message="Thank you for your enquiry. We will be in touch shortly."
        className="mt-8"
      />
    );

  return (
    <form className={`flex grow flex-col text-sm`} onSubmit={handleSubmit}>
      {generalError && <p className="mt-4 text-red-500">{generalError}</p>}

      <div className="flex w-full grow flex-col md:flex-row md:gap-8">
        <div className="mt-3 flex grow flex-col">
          <label htmlFor="firstname">First Name*</label>
          <input
            type="text"
            className={`border-1 mt-1 w-full rounded-md border border-lightgrey bg-purpleblue p-1`}
            id="firstname"
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
          />
          {fieldErrors.firstname && (
            <p className="text-red-500">{fieldErrors.firstname}</p>
          )}
        </div>
        <div className="mt-3 flex grow flex-col">
          <label htmlFor="lastname">Last Name*</label>
          <input
            type="text"
            className={`border-1 mt-1 w-full rounded-md border border-lightgrey bg-purpleblue p-1`}
            id="lastname"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
          />
          {fieldErrors.lastname && (
            <p className="text-red-500">{fieldErrors.lastname}</p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-col">
        <label htmlFor="email">Email*</label>
        <input
          type="email"
          className={`border-1 mt-1 w-full rounded-md border border-lightgrey bg-purpleblue p-1`}
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {fieldErrors.email && (
          <p className="text-red-500">{fieldErrors.email}</p>
        )}
      </div>

      <div className="gap-8 md:flex">
        <div className="mt-3 flex flex-col md:w-1/2">
          <label htmlFor="phone">Phone*</label>
          <input
            type="tel"
            className={`border-1 mt-1 w-full rounded-md border border-lightgrey bg-purpleblue p-1`}
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {fieldErrors.phone && (
            <p className="text-red-500">{fieldErrors.phone}</p>
          )}
        </div>

        <div className="mt-3 flex flex-col md:w-1/2">
          <label htmlFor="contactMethod">Preferred contact method</label>
          <div className="mt-1 flex gap-6 md:mt-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="email"
                name="contactMethod"
                value="email"
                checked={contactMethod === "email"}
                onChange={() => setContactMethod("email")}
                className={`size-5 cursor-pointer bg-purpleblue accent-orange`}
              />
              <label htmlFor="email">Email</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="phone"
                name="contactMethod"
                value="phone"
                checked={contactMethod === "phone"}
                onChange={() => setContactMethod("phone")}
                className={`size-5 cursor-pointer accent-orange`}
              />
              <label htmlFor="phone">Phone</label>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-auto flex-col">
        <div className="flex flex-row justify-between">
          <label htmlFor="message">How can we help you?*</label>
          <label
            htmlFor="message"
            className="self-end text-right text-[0.65rem] text-gray-300"
          >
            Max 500 characters
          </label>
        </div>
        <textarea
          className={`border-1 dynamic-focus-border mt-2 resize-none rounded-md border border-lightgrey bg-purpleblue p-1`}
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        {fieldErrors.message && (
          <p className="text-red-500">{fieldErrors.message}</p>
        )}
      </div>

      {/* v2 reCAPTCHA */}
      {/* <ReCAPTCHA
        sitekey={process.env.RECAPTCHA_SITE_KEY || " "}
        ref={recaptchaRef}
        onChange={handleCaptchaSubmission}
        className="mt-6 flex flex-col self-center"
      /> */}

      <CustomButton
        type="submit"
        size="lg-flat"
        className="mx-auto mt-8 w-3/5 min-w-[15rem]"
      >
        Submit enquiry
      </CustomButton>
    </form>
  );
}

function buildSendGridMessage(formData: FormData): any {
  const msg = {
    templateId: "d-d7b423f70f024a218eedceff9b2cddc1",
    dynamicTemplateData: {
      name: formData.firstname + " " + formData.lastname,
      email: formData.email,
      phone: formData.phone,
      pref: formData.contactMethod,
      message: formData.message,
    },
  };

  return msg;
}
